/**
 * The chat thread: the only scroller in the chat column (CHAT-1). Empty
 * conversations show the sample preview and the upload actions; otherwise
 * the message list, keyed by id so the streaming bubble updates in place
 * (CHAT-6), and following the bottom while the user is near it.
 */
import React, { type MutableRefObject, type ReactElement, type RefObject } from 'react';
import { FlatList, ScrollView, TouchableOpacity, View, type ListRenderItem } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import { useTranslation } from '../../../hooks/useTranslation';
import type { ChatMessage } from '../../../types';
import { threadScrollStyle, webChatStyles } from '../chatStyles';
import { SampleChatPreview } from './SampleChatPreview';

export interface ChatThreadProps {
  messages: ChatMessage[];
  isPhone: boolean;
  isCompact: boolean;
  /** File-summary panel shown at the top of the thread below desktop width. */
  inlineContextPanel: ReactElement | null;
  flatListRef: RefObject<FlatList>;
  renderMessage: ListRenderItem<ChatMessage>;
  /** Set while the user is within 80px of the bottom. */
  shouldAutoScrollRef: MutableRefObject<boolean>;
  pendingScrollToBottomRef: MutableRefObject<boolean>;
  queueScrollToBottom: (animated?: boolean) => void;
  onOpenSample: () => void;
  onUploadDocument: () => void;
  onUploadAudio: () => void;
}

export function ChatThread({
  messages,
  isPhone,
  isCompact,
  inlineContextPanel,
  flatListRef,
  renderMessage,
  shouldAutoScrollRef,
  pendingScrollToBottomRef,
  queueScrollToBottom,
  onOpenSample,
  onUploadDocument,
  onUploadAudio,
}: ChatThreadProps) {
  const { t } = useTranslation();

  return (
    <View style={webChatStyles.thread} testID="chat_thread">
      {messages.length === 0 ? (
        <ScrollView
          style={[{ flex: 1, minHeight: 0 }, threadScrollStyle]}
          contentContainerStyle={[webChatStyles.emptyThread, isCompact && webChatStyles.emptyThreadCompact]}
          keyboardShouldPersistTaps="handled"
        >
          {inlineContextPanel}
          <SampleChatPreview onOpen={onOpenSample} />
          <View style={[webChatStyles.emptyActions, isCompact && webChatStyles.emptyActionsCompact]}>
            <TouchableOpacity style={webChatStyles.primaryReviewButton} onPress={onUploadDocument}>
              <MaterialIcons name="upload-file" size={18} color="#FFFFFF" />
              <Text className="text-white" style={webChatStyles.primaryReviewButtonText}>
                {t('dashboard.webUploadCta')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={webChatStyles.secondaryReviewButton}
              onPress={onUploadAudio}
            >
              <MaterialIcons name="audiotrack" size={18} color={reviewDesk.accent} />
              <Text style={webChatStyles.secondaryReviewButtonText}>
                {t('dashboard.uploadAudio')}
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      ) : (
        <FlatList
          ref={flatListRef}
          data={messages}
          renderItem={renderMessage}
          // Key by id only (CHAT-6): the streaming bubble keeps one id
          // while its text grows, so it is updated, not remounted.
          keyExtractor={(item) => String(item.id)}
          ListHeaderComponent={inlineContextPanel}
          style={[{ flex: 1, minHeight: 0 }, threadScrollStyle]}
          contentContainerStyle={[
            webChatStyles.messagesContent,
            isCompact && webChatStyles.messagesContentCompact,
            isPhone && webChatStyles.messagesContentPhone,
          ]}
          showsVerticalScrollIndicator={false}
          removeClippedSubviews={false}
          keyboardShouldPersistTaps="always"
          keyboardDismissMode="none"
          nestedScrollEnabled
          scrollEventThrottle={16}
          onScroll={(e) => {
            const { contentSize, layoutMeasurement, contentOffset } = e.nativeEvent;
            const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
            shouldAutoScrollRef.current = distanceFromBottom < 80;
          }}
          onContentSizeChange={() => {
            if (pendingScrollToBottomRef.current || shouldAutoScrollRef.current) {
              queueScrollToBottom(true);
            }
          }}
        />
      )}
    </View>
  );
}
